import { useEffect, useState } from 'react';
import { ArrowUpRight, History } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { useVendorPlan } from '../../../common/hooks/use-vendor-plan.hook';
import { formatDate } from '../admin/format';
import { dayMs, localToday } from '../analytics/period';
import { usePlanLimitStore } from './plan-limit-store';
import { useUpgrade } from './useUpgrade';

/**
 * The plan's analytics history window, in days — undefined when unlimited.
 * Pages hand it to `PeriodControl` (`maxDays`) and to `fitPeriod`.
 */
export function useHistoryWindow(): { maxDays: number | undefined } {
  const { getLimit } = useVendorPlan();
  const days = typeof getLimit === 'function' ? getLimit('analyticsHistoryDays') : -1;
  return { maxDays: days > 0 ? days : undefined };
}

/**
 * One line under a report's header when the plan's history window cut what
 * it reads: "Free shows the last 30 days — Unlock 12 months".
 *
 * Shown when the API said it clamped (`historyClampedTo` on a response since
 * this period was chosen — the comparison period reaches back too), or when
 * the chosen range itself runs past the window.
 */
export function HistoryLimitNotice({
  period,
  maxDays: maxDaysProp,
  className,
}: {
  /** The period on screen; a change of period starts listening for clamps afresh. */
  period?: { startDate: string; days?: number };
  maxDays?: number;
  className?: string;
}) {
  const { t } = useTranslation();
  const upgrade = useUpgrade();
  const { maxDays: planDays } = useHistoryWindow();
  const clamp = usePlanLimitStore((s) => s.clamp);
  const [since, setSince] = useState(() => Date.now());
  const startDate = period?.startDate;

  // A clamp from before this period was chosen is about another question.
  useEffect(() => setSince(Date.now() - 50), [startDate]);

  const clampedTo = clamp && clamp.at >= since ? clamp.clampedTo : null;
  const fromClamp = clampedTo ? Math.round((dayMs(localToday()) - dayMs(clampedTo)) / 86_400_000) + 1 : undefined;
  const maxDays = maxDaysProp ?? planDays ?? fromClamp;
  const earliestMs = maxDays ? dayMs(localToday()) - (maxDays - 1) * 86_400_000 : undefined;
  const exceeds = !!startDate && !!earliestMs && dayMs(startDate) < earliestMs;

  if (!maxDays || (!clampedTo && !exceeds)) return null;

  return (
    <div
      role="note"
      data-testid="history-limit-notice"
      className={cn('flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 rounded-xl border bg-card px-4 py-2.5 text-sm', className)}
    >
      <span className="flex min-w-0 items-center gap-2 text-muted-foreground">
        <History className="size-3.5 shrink-0" aria-hidden />
        <span>
          {t('FREE_PLAN.HISTORY.NOTICE', { count: maxDays })}
          {clampedTo && <span className="ms-1">{t('FREE_PLAN.HISTORY.CLAMPED', { date: formatDate(new Date(dayMs(clampedTo))) })}</span>}
        </span>
      </span>
      <button
        type="button"
        onClick={() => upgrade('analyticsHistoryDays')}
        className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-foreground underline-offset-2 hover:underline"
        data-testid="history-limit-notice-cta"
      >
        {t('FREE_PLAN.HISTORY.CTA')}
        <ArrowUpRight className="size-3 rtl:-scale-x-100" aria-hidden />
      </button>
    </div>
  );
}
